import clsx from 'clsx';
import { PieChart, Tooltip, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useFormatGraphData from '@/hooks/charts/useFormatGraphData';

// Components
import TooltipCustom from './components/TooltipCustom';
import ChartWrapper from './ChartWrapper';
import Icon from '@/components/base/Icon';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const SingleIconPieChartComponent = ({
    className,
    data,
    activeTable,
    userNumbersFormat,
    isExport,
    hiddenKeys,
    height = 195
}) => {
    const { isDarkMode } = useDarkMode();
    const { t } = useTranslation('analytics');
    const { formatPercentValue } = useFormatGraphData();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';

    const finalData = useMemo(() => {
        let finalData = [];
        if (data?.graphData?.[0]) {
            Object.entries(data.graphData[0])?.map((item) => {
                if (!hiddenKeys?.[item[0]]) {
                    finalData.push({
                        type: item[0],
                        value: item[1],
                        percentage_formatted: formatPercentValue(item[1], data.graphData)
                    });
                }
            });
        }
        return finalData;
    }, [data, formatPercentValue, hiddenKeys]);

    const finalColumnNames = useMemo(() => {
        let obj = {};

        if (finalData.length) {
            Object.entries(finalData[0])?.map((item) => {
                obj[item[0]] = t(data.name[item[0]]);
            });
        }

        return obj;
    }, [data.name, finalData, t]);

    return (
        <div className={clsx(className)}>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={finalData}
                    columnNames={finalColumnNames}
                    valueFormat={data.valueFormat.notation !== 'compact' ? data.valueFormat : {}}
                    userNumbersFormat={userNumbersFormat}
                    formatterTranslation={data?.tableColumnFormatterTranslation ?? null}
                />
            ) : (
                <div
                    className="relative h-full w-full overflow-x-auto"
                    style={{
                        minHeight: isExport ? 140 : height,
                        width: isExport ? data.fixedSize : '100%'
                    }}>
                    <div className="absolute left-0 top-0 z-1 h-full w-full">
                        <ResponsiveContainer
                            height={isExport ? 140 : height}
                            width={isExport ? data.fixedSize : '100%'}>
                            <PieChart margin={data.margin}>
                                <Tooltip
                                    cursor={false}
                                    content={
                                        <TooltipCustom
                                            data={data}
                                            tooltipTranslation={data.tooltipTranslation}
                                            valueFormat={
                                                data.valueFormat.notation !== 'compact'
                                                    ? data.valueFormat
                                                    : {}
                                            }
                                        />
                                    }
                                />
                                <Pie
                                    isAnimationActive={!isExport}
                                    data={finalData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    startAngle={90}
                                    endAngle={450}
                                    outerRadius="100%"
                                    innerRadius="80%"
                                    stroke="0">
                                    {finalData.map((item) => {
                                        return (
                                            <Cell
                                                key={`cell-${item.type}`}
                                                fill={
                                                    !hiddenKeys?.[item.type]
                                                        ? darkModeColorsChart[theme][
                                                              data.color[item.type]
                                                          ]
                                                        : darkModeColorsChart[theme][
                                                              'circlestrokemap'
                                                          ]
                                                }
                                            />
                                        );
                                    })}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    {data.icon ? (
                        <div
                            className={clsx(
                                'absolute left-1/2 top-1/2 flex h-13 w-13 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-icoGray-300',
                                { 'dark:bg-icoDarkMode-deepBlack': !isExport }
                            )}>
                            <Icon
                                icon={data.icon}
                                className={clsx('text-icoGray-500', {
                                    'text-2xl dark:text-white': !isExport,
                                    'text-xl': isExport
                                })}
                            />
                        </div>
                    ) : null}
                    {data.IconComponent ? <data.IconComponent /> : null}
                </div>
            )}
        </div>
    );
};

SingleIconPieChartComponent.propTypes = {
    data: PropTypes.object,
    activeTable: PropTypes.bool,
    userNumbersFormat: PropTypes.object,
    height: PropTypes.number,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const SingleIconPieChart = ({
    data,
    onReset = () => {
        /** */
    },
    isLoading = false,
    className = '',
    isExport,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        isExport={isExport}
        legendType="square"
        {...restProps}>
        <SingleIconPieChartComponent
            className={clsx('w-full', {
                'mb-11': !isExport,
                'mb-5': isExport
            })}
        />
    </ChartWrapper>
);

SingleIconPieChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { SingleIconPieChartComponent, SingleIconPieChart };
